import { useEffect, useRef } from 'react';
import { useLogGoodsAnalyticsEvent } from './useLogGoodsAnalyticsEvent';
import { bridge, isNil, isNotNil, localStorageBridge } from 'shared/src/utils';
import { useRetainedEffect } from 'shared/src/hooks/retainedState';
import { useGoodsDetail } from '../queries/useGoodsDetail';
import { useGoodsBasic } from '../queries/useGoodsBasic';
import { useGoodsDiscounts } from '../queries/useGoodsDiscounts';
import { useGoodsCoupon } from '../queries/useGoodsCoupon';
import { useGoodsReviewSummary } from '../queries/useGoodsReviewSummary';
import { EvaluationText } from 'shared/src/constant/domain/review/EvaluationText';
import { useGoodsRoute } from '../../../hooks/useGoodsRoute';
import { useEntryAdInfo } from './useEntryAdInfo';

export const useLogViewGoodsDetail = () => {
  const logGoodsAnalytics = useLogGoodsAnalyticsEvent();
  const { params } = useGoodsRoute<'GoodsDetail'>();
  const { data: goodsDetail } = useGoodsDetail();
  const { data: goodsBasic } = useGoodsBasic();
  const { data: goodsDiscounts, isPending: pendingGoodsDiscounts } =
    useGoodsDiscounts();
  const { data: goodsCoupon, isPending: pendingGoodsCoupon } = useGoodsCoupon();
  const { data: goodsReviewSummary, isPending: pendingGoodsReviewSummary } =
    useGoodsReviewSummary();
  const entryAd = useEntryAdInfo();

  // VIEW 임계경로에서 네이티브 await 를 제거하기 위해 마운트 시 미리 읽어둔다.
  // localStorage 읽기는 게이팅 API 들보다 훨씬 빨리 끝나므로 발화 시점엔 준비됨.
  // (아직 못 읽었으면 기존과 동일하게 true 로 default)
  const isExpandMaxDiscountRef = useRef(true);
  useEffect(() => {
    let cancelled = false;
    localStorageBridge
      .getBoolean('IS_EXPANDED_MAX_DISCOUNT_IN_GOODS_DETAIL')
      .then(value => {
        if (!cancelled && value != null) {
          isExpandMaxDiscountRef.current = value;
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // iOS의 visibility?.hasReviewSummary / Android의 componentVisibility?.reviewSummary ?: true
  // 와 동일하게, review_summary 가 false 인 상품은 review API 결과를 기다리지 않고 발화.
  // visibility 필드가 없으면 안전하게 true 로 default.
  const hasReviewSummary =
    goodsDetail?.component_visibility.review_summary ?? true;

  useRetainedEffect(
    'view-goods-detail',
    () => {
      if (isNil(goodsDetail)) return false;
      if (isNil(goodsBasic)) return false;

      // 네이티브(iOS catchAndReturn(nil) / Android)와 정합: 할인/쿠폰/리뷰요약은 "성공"이
      // 아니라 "완료(성공 or 실패)"까지만 기다린다. 실패해도 해당 파라미터만 빠지고 VIEW 는 발화.
      // (retry:0 이라 기존 isSuccess 게이트는 이 API 들이 5xx 나면 VIEW 를 영구 누락시켰음)
      if (pendingGoodsDiscounts) {
        return false;
      }
      if (pendingGoodsCoupon) {
        return false;
      }
      if (hasReviewSummary && pendingGoodsReviewSummary) {
        return false;
      }

      //리다이렉트 처리 하기 때문에 log view goods detail은 처리하지 않음.
      if (isNotNil(goodsBasic.redirect_deeplink)) {
        return false;
      }

      const categories = goodsBasic.display_categories?.[0] ?? null;

      (async () => {
        const isExpandMaxDiscount =
          (await localStorageBridge.getBoolean(
            'IS_EXPANDED_MAX_DISCOUNT_IN_GOODS_DETAIL',
          )) ?? true;
        const getLoggingAnalytics = () => {
          if (typeof params.extras === 'string') {
            return JSON.parse(params.extras).logging_analytics;
          }

          if (typeof params.extras?.logging_analytics === 'string') {
            return params.extras?.logging_analytics
              ? JSON.parse(params.extras.logging_analytics)
              : undefined;
          }

          return params.extras?.logging_analytics ?? {};
        };

        const result = logGoodsAnalytics({
          name: 'VIEW_GOODS_DETAIL',
          channel: ['firebase', 'facebook', 'amplitude', 'braze', 'ablylog'],
          analytics: {
            ...goodsDetail.logging.analytics,
            ...(goodsDiscounts?.logging.analytics ?? {}),
            MARKET_TYPE_SNO:
              goodsBasic.market.market_vertical_type.sno.toString(),
            PARENT_CATEGORY_SNO: categories?.parent_category.sno.toString(),
            PARENT_CATEGORY_NAME: categories?.parent_category.name,
            IS_EXIST_USABLE_COUPONS: goodsCoupon?.is_exist_usable_coupons
              ? '1'
              : '0',
            IS_EXIST_DOWNLOADABLE_COUPONS: isNotNil(goodsCoupon?.coupon)
              ? '1'
              : '0',
            EXPECTED_DELIVERY_DATE_PROBABILITY:
              goodsDetail.goods.send_expectation_info?.percentage,
            SALE_TYPE: goodsBasic.sale_type,
            IS_EXIST_SIZE_TAB:
              goodsDetail.tabs.filter(tab => tab.tab_type === 'SIZE').length > 0
                ? '1'
                : '0',
            IS_SELLER_DELIVERY_LEADTIME:
              goodsDetail.goods.send_expectation_info?.is_market_provided ??
              false,
            IS_EXIST_DELAY_CONTENTS: goodsBasic.is_delayed ?? false,
            REVIEW_SUMMARY_SNOS: hasReviewSummary
              ? goodsReviewSummary?.review_summary.map(review => review.sno)
              : undefined,
            NUDGING_DESCRIPTION: goodsBasic.price_description?.text,
            IS_TODAY_FINISHED_COUPON: isNotNil(
              goodsCoupon?.coupon_cta_info?.cta_description,
            )
              ? '1'
              : '0',
            FIRST_REVIEW_SUMMARY_EVALUATION:
              hasReviewSummary && goodsReviewSummary?.review_summary?.[0]?.eval
                ? EvaluationText[goodsReviewSummary.review_summary[0].eval]
                : undefined,
            IS_OPENED_DISCOUNTED_TOGGLE: isExpandMaxDiscount ? '1' : '0',
            RESTORE: '0',
            IS_AD: entryAd != null ? '1' : '0',
            ...getLoggingAnalytics(),
            TROPHY: goodsDetail.goods.market.trophies?.[0]?.title,
            DELIVERY_LEAD_TIME:
              goodsDetail.goods.send_expectation_info?.days ?? undefined,
            // HAS_STACKABLE_COUPON:
            //   goodsDiscounts?.logging.analytics ??,
          },
        });

        if (result) {
          bridge.logAnalyticsEvent({
            name: 'airbridge.ecommerce.product.viewed',
            channel: ['airbridge'],
            analytics: {
              ACTION: goodsBasic.market.market_vertical_type.name,
              LABEL: categories?.name,
              VALUE: goodsBasic.price_info.thumbnail_price,
              CURRENCY: 'KRW',
              PRODUCTS: [
                {
                  PRODUCT_ID: goodsBasic.sno.toString(),
                  PRODUCT_NAME: goodsBasic.name,
                  PRODUCT_CURRENCY: 'KRW',
                  PRODUCT_PRICE: goodsBasic.price_info.thumbnail_price,
                  PRODUCT_BRAND_ID: goodsBasic.market.sno.toString(),
                  PRODUCT_BRAND_NAME: goodsBasic.market.name,
                },
              ],
            },
          });

          bridge.logAnalyticsEvent({
            name: 'fb_mobile_content_view',
            channel: ['facebook'],
            analytics: {
              fb_content_type: 'product',
              fb_content_id: `["${goodsBasic.sno}"]`,
              fb_currency: 'KRW',
              _valueToSum: goodsBasic.price_info.thumbnail_price,
            },
          });
        }
      })();
    },
    [
      goodsBasic,
      goodsCoupon,
      goodsDetail,
      goodsDiscounts,
      goodsReviewSummary,
      hasReviewSummary,
      logGoodsAnalytics,
      params.extras,
      pendingGoodsReviewSummary,
      pendingGoodsCoupon,
      pendingGoodsDiscounts,
      entryAd,
    ],
  );
};
